import { supabase } from '../utils/supabase/client';

class TagServiceFirebase {
  constructor() {
    // Detectar se está rodando no Vercel ou localmente
    this.isVercel = typeof window !== 'undefined' && 
      (window.location.protocol === 'https:' || 
       window.location.hostname.includes('vercel.app') || 
       window.location.hostname.includes('vercel.com') ||
       window.location.hostname.includes('gpreto.space') ||
       window.location.hostname !== 'localhost');
    
  }

  // Criar uma nova tag no Firebase
  async createTag(tagData, factoryId) {
    try {

      // Verificar se já existe uma tag com o mesmo nome na mesma divisão
      const existingTags = await this.getTags(null, tagData.division);
      const duplicateTag = existingTags.find(tag => {
        const tagDataToCheck = tag.tagData || tag;
        return tagDataToCheck.name === tagData.name && tagDataToCheck.division === tagData.division;
      });
      
      if (duplicateTag) {
        return { 
          success: false, 
          message: 'Tag já existe', 
          existingTag: duplicateTag 
        };
      }

      const timestamp = new Date().toISOString();
      const { id: _id, isNewTag: _isNewTag, ...tagDataWithoutId } = tagData;
      const { data, error } = await supabase
        .from('tags')
        .insert([{
          ...tagDataWithoutId,
          factoryId: factoryId || null,
          createdAt: timestamp,
          updatedAt: timestamp
        }])
        .select('id');

      if (error) {
        throw error;
      }

      const result = { success: true, id: data?.[0]?.id, ...tagData };
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Buscar tags do localStorage como fallback
  getTagsFromLocalStorage(factoryId = null, division = null) {
    try {
      let tags = [];
      
      if (factoryId) {
        // Buscar tags específicas da fábrica
        const savedTags = localStorage.getItem(`tags_${factoryId}`);
        if (savedTags) {
          const factoryTags = JSON.parse(savedTags);
          tags = [
            ...(factoryTags.regiao || []),
            ...(factoryTags.material || []),
            ...(factoryTags.outros || []),
            ...(factoryTags.tipoProduto || [])
          ];
        }
      } else {
        // Buscar todas as tags globais
        const globalTags = localStorage.getItem('globalTags');
        if (globalTags) {
          const parsedGlobalTags = JSON.parse(globalTags);
          tags = [
            ...(parsedGlobalTags.regiao || []),
            ...(parsedGlobalTags.material || []),
            ...(parsedGlobalTags.outros || []),
            ...(parsedGlobalTags.tipoProduto || [])
          ];
        }
      }
      
      // Filtrar por divisão se especificado
      if (division) {
        tags = tags.filter(tag => tag.division === division);
      }
      
      return {
        success: true,
        data: tags,
        count: tags.length,
        fallback: true
      };
    } catch (error) {
      return {
        success: true,
        data: [],
        count: 0,
        fallback: true,
        error: error.message
      };
    }
  }

  // Buscar tags do Firebase
  async getTags(factoryId = null, division = null) {
    try {
      // Buscar tags do Supabase (log removido para reduzir poluição do console)
      let query = supabase.from('tags').select('*');

      if (factoryId) {
        query = query.eq('factoryId', factoryId);
      }
      if (division) {
        query = query.eq('division', division);
      }

      const { data, error } = await query;

      if (error) {
        return this.getTagsFromLocalStorage(factoryId, division);
      }

      const result = { ok: true, data, fallback: false };

      const extractedTags = (result.data || []).map(item => {
        // Verificar se tem estrutura tagData ou se é plano
        const tagData = item.tagData || item;
        
        // Para backups de associação, usar tagName e tagDivision
        const name = tagData.name || item.tagName || item.name;
        const division = tagData.division || item.tagDivision || item.division;
        
        return {
          id: item.id,
          name: name,
          division: division,
          createdAt: tagData.createdAt || item.createdAt,
          updatedAt: tagData.updatedAt || item.updatedAt,
          factoryId: item.factoryId || null,
          type: item.type || null
        };
      }).filter(tag => tag.name && tag.division); // Filtrar tags válidas
      
      return extractedTags;
    } catch (error) {
      return this.getTagsFromLocalStorage(factoryId, division);
    }
  }

  // Atualizar uma tag no Firebase
  async updateTag(tagId, tagData) {
    try {

      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from('tags')
        .update({ ...tagData, updatedAt: timestamp })
        .eq('id', tagId)
        .select('id');

      if (error) {
        throw error;
      }

      const result = { success: true, id: data?.[0]?.id, ...tagData };
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Deletar uma tag do Firebase
  async deleteTag(tagId) {
    try {

      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', tagId);

      if (error) {
        throw error;
      }

      const result = { success: true, id: tagId };
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Associar uma tag global existente a uma fábrica (versão simplificada)
  async associateTagToFactory(tagId, factoryId) {
    try {

      // Como a API de associação não existe, vamos apenas retornar sucesso
      // A associação real acontece quando a tag é criada com factoryId
      
      return { 
        success: true, 
        message: 'Tag associada com sucesso (simulado)',
        tagId: tagId,
        factoryId: factoryId
      };
    } catch (error) {
      throw error;
    }
  }

  // Obter tags de uma fábrica específica
  async getFactoryTags(factoryId) {
    try {
      const tags = await this.getTags(factoryId);
      
      // Organizar tags por divisão
      const organizedTags = {
        regiao: [],
        material: [],
        outros: [],
        tipoProduto: []
      };

      // Garantir que tags é um array
      const tagsArray = Array.isArray(tags) ? tags : [];
      
      tagsArray.forEach(tag => {
        // Extrair dados reais de tagData se existir
        const tagData = tag.tagData || tag;
        const division = tagData.division;
        
        if (division && organizedTags[division]) {
          // Criar objeto tag com estrutura correta
          const processedTag = {
            id: tag.id,
            name: tagData.name,
            division: division,
            createdAt: tag.createdAt || tagData.createdAt,
            updatedAt: tag.updatedAt || tagData.updatedAt,
            factoryId: tag.factoryId
          };
          
          organizedTags[division].push(processedTag);
        }
      });

      return organizedTags;
    } catch (error) {
      return { regiao: [], material: [], outros: [], tipoProduto: [] };
    }
  }

  // Obter todas as tags globais (sem filtro de fábrica)
  async getAllGlobalTags() {
    try {
      const tags = await this.getTags();
      
      // Organizar tags por divisão
      const organizedTags = {
        regiao: [],
        material: [],
        outros: [],
        tipoProduto: []
      };

      // Garantir que tags é um array
      const tagsArray = Array.isArray(tags) ? tags : [];
      
      tagsArray.forEach((tag, index) => {
        
        // Extrair dados reais de tagData se existir
        const tagData = tag.tagData || tag;
        
        // Para backups de associação, usar tagName e tagDivision
        const name = tagData.name || tag.tagName || tag.name;
        const division = tagData.division || tag.tagDivision || tag.division;
        
        
        // Filtrar apenas tags válidas e não backups de associação
        if (division && name && organizedTags[division] && tag.type !== 'association_backup') {
          // Criar objeto tag com estrutura correta
          const processedTag = {
            id: tag.id,
            name: name,
            division: division,
            createdAt: tag.createdAt || tagData.createdAt,
            updatedAt: tag.updatedAt || tagData.updatedAt,
            factoryId: tag.factoryId
          };
          
          organizedTags[division].push(processedTag);
        } else if (tag.type === 'association_backup') {
        } else {
        }
      });

      return organizedTags;
    } catch (error) {
      return { regiao: [], material: [], outros: [], tipoProduto: [] };
    }
  }

  // Testar conexão com Firebase
  async testConnection() {
    try {
      
      // Tentar fazer uma requisição simples para verificar se a API está funcionando
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .limit(1);

      if (error) {
        return { success: false, error: error.message, details: error.details };
      }

      return { success: true, message: 'Supabase connection OK', data };
    } catch (error) {
      return { success: false, error: 'Connection failed', details: error.message };
    }
  }
}

const tagServiceFirebase = new TagServiceFirebase();

// Verificar se todos os métodos estão disponíveis
if (!tagServiceFirebase.createTag) {
}

export default tagServiceFirebase;
